import { useEffect, useReducer } from 'react';
import { ApiError } from '../../lib/api';
import { useQuery } from '../../lib/query';

/** GET /api/public/plans — public catalog DTO (see .orchestration/perzo-brand-status.md §7). */
export interface PublicPlan {
  key: string;
  name: string;
  currency: string;
  monthlyPriceCents: number;
  includedCredits: number;
  overagePriceCents: number;
  /** Free-trial days, present only when the plan has a trial (plan_versions.trial_days > 0). */
  trialDays?: number;
}

export interface PublicPlansResponse {
  plans: PublicPlan[];
  billing?: { unit?: string; overage?: string };
  /** appStoreUrl: Perzo's Shopify App Store listing, only once installs are open and it is configured. */
  shopify?: { installAvailable?: boolean; installPath?: string; shopParam?: string; appStoreUrl?: string };
}

export const PUBLIC_PLANS_PATH = '/api/public/plans';

/** How often an open page asks for the plans again (the server's catalog lives 30 s). */
export const PLANS_REFRESH_MS = 60_000;
/** Plans not confirmed for this long (refreshes failing or hanging) are no longer shown, nor the trial or offers. */
export const PLANS_MAX_AGE_MS = 5 * 60_000;
/** A plans request that has not answered by then fails, so the next refresh can run. */
export const PLANS_REQUEST_TIMEOUT_MS = 10_000;

const NOT_CONFIRMED = new ApiError(0, 'plans_not_confirmed', 'Current plans could not be confirmed.');

/**
 * The public plans DTO, refreshed while the page is open. Its last answer expires by time, PLANS_MAX_AGE_MS after
 * it arrived, whatever the refreshes are doing (failing or still pending): a timer re-renders at that moment, and
 * prices, trial, install state and JSON-LD offers are never shown from older data (the page shows its unavailable
 * state instead) until a refresh succeeds.
 */
export function usePublicPlans() {
  const query = useQuery<PublicPlansResponse>(PUBLIC_PLANS_PATH, { refetchInterval: PLANS_REFRESH_MS, timeoutMs: PLANS_REQUEST_TIMEOUT_MS });
  // updatedAt 0: the server render's own data (no browser age yet).
  const expiresAt = query.updatedAt > 0 ? query.updatedAt + PLANS_MAX_AGE_MS : 0;
  const [, expire] = useReducer((n: number) => n + 1, 0);
  const { refetch } = query;
  useEffect(() => {
    if (!expiresAt) return;
    const timer = window.setTimeout(() => {
      expire();
      void refetch();
    }, Math.max(0, expiresAt - Date.now()) + 50);
    return () => window.clearTimeout(timer);
  }, [expiresAt, refetch]);
  const expired = expiresAt > 0 && Date.now() >= expiresAt;
  return expired ? { ...query, data: undefined, error: query.error ?? NOT_CONFIRMED } : query;
}

/** Whether stores can install Perzo now (until then, calls to action invite them to get early access). */
export function useInstallOpen(): boolean {
  return usePublicPlans().data?.shopify?.installAvailable === true;
}

/** Where an install button leads when installs are open but no listing is configured: the App Store search. */
export const APP_STORE_SEARCH = 'https://apps.shopify.com/search?q=perzo';

/** The App Store listing to install from, only an https://apps.shopify.com address (never any other link). */
export function appStoreListing(data: PublicPlansResponse | undefined): string | null {
  const url = data?.shopify?.installAvailable === true ? data.shopify.appStoreUrl : undefined;
  return typeof url === 'string' && /^https:\/\/apps\.shopify\.com\/[a-z0-9][a-z0-9-]{0,99}$/.test(url) ? url : null;
}

/** Whether the live demo (/try) is switched on. Links to it stay hidden until it is. */
export function useSiteDemoEnabled(): boolean {
  return useQuery<{ enabled?: boolean }>('/api/public/demo').data?.enabled === true;
}

/**
 * The free trial the site may advertise: the same positive whole number of days on every public plan, from the plans
 * DTO only (never hardcoded; the server's content/cta.ts trialFromPlans applies the same rule).
 */
export function trialDaysOf(data: PublicPlansResponse | undefined): number | null {
  const plans = validPlans(data);
  const first = plans[0]?.trialDays;
  if (typeof first !== 'number' || !Number.isInteger(first) || first <= 0) return null;
  return plans.every((p) => p.trialDays === first) ? first : null;
}

/** While installs are closed the trial is a launch condition, never something an early-access sign-up starts. */
export function trialLabel(days: number, installOpen: boolean): string {
  return installOpen ? `${days}-day free trial` : `${days}-day free trial at launch`;
}

export function useTrialDays(): number | null {
  return trialDaysOf(usePublicPlans().data);
}

/** Only well-formed plans are shown; nothing is filled in or estimated. */
export function validPlans(data: PublicPlansResponse | undefined): PublicPlan[] {
  const list = Array.isArray(data?.plans) ? data.plans : [];
  return list.filter(
    (plan) =>
      plan &&
      typeof plan.name === 'string' &&
      typeof plan.currency === 'string' &&
      Number.isFinite(plan.monthlyPriceCents) &&
      Number.isFinite(plan.includedCredits) &&
      Number.isFinite(plan.overagePriceCents),
  );
}
